import _ from 'lodash';

import {normalizeValue} from './StyleStoreUtils';

import * as ShadowParser from '../parsers/ShadowParser';
import * as TransformParser from '../parsers/TransformParser';
import * as TransitionParser from '../parsers/TransitionParser';
import * as FilterParser from '../parsers/FilterParser';
import * as BgImageParser from '../parsers/BgImageParser';
import * as FontVariationSettingsParser from '../parsers/FontVariationSettingsParser';

import * as StringUtils from '@packages/utilities/string-utils';
import {generateGuid} from '@packages/utilities/guid';
import {UPSTREAM, LOCAL, DOWNSTREAM, BROWSER} from '../constants/StreamTypes';

// Slices away parts that have no definitive style basis, meaning they apply to nothing
// This is mainly to handle several cases where this situation can arise (including sites with broken styles)
// e.g. a background-position for a part that has neither local or upstream image (because it was deleted upstream)
// Also pads the front of a transform with a missing perspective property when the upstream has it
// @return {Part[]}
export function _sliceParts(parts, type) {
  parts = _.clone(parts);

  // If the local parts are longer than the upstream parts, filter out all the dangling local parts that have no properties that would make them result in a real local part
  const numLocalParts = parts.local ? parts.local.length : 0;
  const numUpstreamParts = parts.upstream ? parts.upstream.length : 0;
  if (numUpstreamParts < numLocalParts) {
    const danglingParts = parts.local.slice(numUpstreamParts);
    const attachedParts = parts.local.slice(0, numUpstreamParts);
    const keepDanglingParts = (function () {
      if (type === 'background') {
        return danglingParts.filter((part) => Boolean(part.local.image));
      } else if (type === 'transition') {
        return danglingParts.filter((part) => Boolean(part.local.property));
      } else {
        return danglingParts;
      }
    })();

    parts.local = attachedParts.concat(keepDanglingParts);
  }

  if (numUpstreamParts > numLocalParts && numLocalParts > 0) {
    if (type === 'transform') {
      if (
        parts.upstream &&
        parts.upstream.length > 0 &&
        parts.upstream[0].upstream.perspective &&
        parts.upstream[0].upstream.perspective.value !== null &&
        parts.local &&
        (parts.local.length === 0 ||
          !parts.local[0].local.perspective ||
          parts.local[0].local.perspective.value === null)
      ) {
        parts.local.unshift(_.clone(parts.upstream[0]));
      }
    }
  }

  return parts;
}

// Decorate the part list with a source rule for the whole thing based on the CSS property it represents
// @return {Part[]}
export function _decoratePartsSources(parts, sourceProperty) {
  // Derive the source of a {local,upstream,downstream} component, based on the CSS property it represents
  // This decoration is necessary for the style tab to display inheritance info/resetting support for the whole part list
  // Mainly a matter of returning the source rule of the definitive property
  // @return {Part[]}
  function _getComponentSource(_parts, stream, _sourceProperty) {
    const component = _parts[stream];
    const componentParts = component && component.value;
    const componentPartsSource = componentParts && componentParts.source;

    // If we found the source decorated on the parts array from the parser (e.g. due to it being an override), we're done
    if (componentPartsSource) {
      return componentPartsSource;
    } else {
      // If there is at least one part that we can find the source from
      const firstPart = componentParts && componentParts[0];
      if (firstPart) {
        // If the first part had the source property we were looking for
        const firstPartProperty = firstPart[_sourceProperty];
        const firstPartPropertySource =
          firstPartProperty && firstPartProperty[stream].source;
        if (firstPartPropertySource) {
          return firstPartPropertySource;
        } else {
          // Otherwise, fall back to querying for a source on any property
          for (const property in firstPart) {
            if (!/^_/.test(property)) {
              const source = firstPart[property][stream].source;

              if (source) {
                return source;
              }
            }
          }

          return null;
        }
      } else {
        return null;
      }
    }
  }

  const {local, upstream, downstream} = parts;

  const result = {};

  if (local) {
    local.source = _getComponentSource(parts, LOCAL, sourceProperty);
    result.local = local;
  }
  if (upstream) {
    upstream.source = _getComponentSource(parts, UPSTREAM, sourceProperty);
    result.upstream = upstream;
  }
  if (downstream) {
    downstream.source = _getComponentSource(parts, DOWNSTREAM, sourceProperty);
    result.downstream = downstream;
  }

  return result;
}

// We want to reuse BgImageParser parsers to fix bugs only once. The data
// models between these two aren’t compatible so we keep this locally
// mutable reference to minimize churn a bit
const sharedMutablePointer = {hack: {}};
const buildSizeValue = (dimension) => (subvalue) => {
  if (!subvalue) return null;
  const {value, source} = subvalue;

  if (value === 'contain' || value === 'cover') {
    return {
      value,
      unit: null,
      source,
    };
  } else {
    const parsed = BgImageParser.parseSize(value, 'hack', sharedMutablePointer)
      .hack[dimension];

    return parsed == null
      ? null
      : typeof parsed === 'string'
        ? {value: parsed, unit: null, source}
        : {value: parsed.value, unit: parsed.unit, source};
  }
};

/**
 * This is a hack to differentiate between solid color and gradient.
 * Solid colors are represented as gradients with:
 * - The "to bottom" keyword value
 * - Two stops with the same colo
 *
 * All other gradients, linear or radial, are represented as gradients with
 * UnitValues, and _not_ keyword values
 */
export const HACK_SOLID_COLOR_VALUE = 'to bottom';

const _parseWidth = buildSizeValue('width');
const _parseHeight = buildSizeValue('height');

// Methods to explode a set of raw part-relevant styleLess CSS values to style-tab compatible resolved component properties
// This proceeds in several passes:
// 1) explode out the {local,upstream,downstream} part component subvalues from the browser's domain (e.g. background-position) to the style tab's domain (e.g. top, left)
// 2a) slice away parts that apply to nothing (e.g. local background-position tweaks of a background-image style that was deleted in the upstream rule)
// 2b) add missing correlatory parts like the implicit perspective part of the transform property
// 3) decoreate the part list with the definitive "source" rule for it
// 4) merge correlations inside the part list subvalues (e.g. "this part's" backgroud-position-top is set locally but also inherits from a correlated upstream part)
export function parseBackgroundParts(value) {
  const parts = BgImageParser.parseParts(value);
  const slicedParts = _sliceParts(parts, 'background');
  const mergedParts = _mergeParts(slicedParts, (part) => {
    const localImage = part.local && part.local.image;
    const upstreamImage = part.upstream && part.upstream.image;
    const isImage =
      (Boolean(localImage) || Boolean(upstreamImage)) &&
      /^(?:@|url\()/.test((localImage || upstreamImage).value);
    const isGradient = (() => {
      if (!isImage) {
        const localGradient = part.local && part.local.gradient;
        const upstreamGradient = part.upstream && part.upstream.gradient;
        const gradient = localGradient || upstreamGradient;
        if (gradient) {
          const isSolidColor =
            gradient.stops.length === 2 &&
            gradient.stops[0].color === gradient.stops[1].color &&
            ((localImage?.value ?? '').startsWith(
              `linear-gradient(${HACK_SOLID_COLOR_VALUE}`
            ) ||
              (upstreamImage?.value ?? '').startsWith(
                `linear-gradient(${HACK_SOLID_COLOR_VALUE}`
              ));
          return !isSolidColor;
        }
        return false;
      }
    })();

    function _parseImage(_part) {
      let collapsedImageValue = _mapPartComponents(_part, function (component) {
        return component && component.imageInfo
          ? {
              value: BgImageParser.FILE_PREFIX + component.imageInfo._id,
              source: component.image.source || null,
            }
          : null;
      });

      if (!(collapsedImageValue.local || collapsedImageValue.upstream)) {
        // HACK: If we got here, that means we cannot resolve imageInfo for either the local or upstream image.
        // This means the image was deleted while still being used and this background part is completely broken and unparsable.
        // So pretend it was the example image (which is wrong) just to prevent the UI from breaking.
        collapsedImageValue = {
          local: {
            value: '@img_example_bg',
          },
        };
      }

      return normalizeValue(collapsedImageValue);
    }

    function _parseTop(subvalue) {
      const match =
        subvalue &&
        subvalue.value.match(
          /^(auto|left|center|right|-?\d*\.?\d+)(\S*) (auto|top|center|bottom|-?\d*\.?\d+)(\S*)$/
        );

      if (match) {
        const stringlyValue = {
          top: {value: 0, unit: 'px'},
          center: {value: 50, unit: '%'},
          bottom: {value: 100, unit: '%'},
          auto: {value: null, unit: null},
        }[match[3]];
        const v = (() => {
          if (stringlyValue) {
            return stringlyValue.value;
          } else {
            return parseFloat(match[3]);
          }
        })();
        const u = (() => {
          if (stringlyValue) {
            return stringlyValue.unit;
          } else {
            return match[4] || null;
          }
        })();
        const {source} = subvalue;

        return {value: v, unit: u, source};
      } else {
        return null;
      }
    }

    function _parseLeft(subvalue) {
      const match =
        subvalue &&
        subvalue.value.match(
          /^(auto|left|center|right|-?\d*\.?\d+)(\S*) (auto|top|center|bottom|-?\d*\.?\d+)(\S*)$/
        );

      if (match) {
        const stringlyValue = {
          left: {value: 0, unit: 'px'},
          center: {value: 50, unit: '%'},
          right: {value: 100, unit: '%'},
          auto: {value: null, unit: null},
        }[match[1]];
        const v = (() => {
          if (stringlyValue) {
            return stringlyValue.value;
          } else {
            return parseFloat(match[1]);
          }
        })();
        const u = (() => {
          if (stringlyValue) {
            return stringlyValue.unit;
          } else {
            return match[2] || null;
          }
        })();
        const {source} = subvalue;

        return {value: v, unit: u, source};
      } else {
        return null;
      }
    }

    // Set the placeholder property of a part
    // @return {Part}
    function _setPartPlaceholder(_part, placeholderValue) {
      _part = _.clone(_part);
      _part.placeholder = placeholderValue;
      return _part;
    }

    if (isImage) {
      return {
        _type: 'background',
        _guid: generateGuid(),
        image: _parseImage(part),
        sourceWidth: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'imageInfo',
              {
                value: 'width',
                source: 'source',
              },
            ])
          )
        ),
        sourceHeight: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'imageInfo',
              {
                value: 'height',
                source: 'source',
              },
            ])
          )
        ),
        sourceSize: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'imageInfo',
              {
                value: [
                  'fileSize',
                  (fileSize) => StringUtils.prettySize(fileSize),
                ],
                source: 'source',
              },
            ])
          )
        ),
        width: normalizeValue(
          _setPartPlaceholder(
            _mapPartComponents(part, _deepContext(['size', _parseWidth])),
            {
              value: 'Auto',
            }
          )
        ),
        height: normalizeValue(
          _setPartPlaceholder(
            _mapPartComponents(part, _deepContext(['size', _parseHeight])),
            {
              value: 'Auto',
            }
          )
        ),
        repeat: normalizeValue(
          _mapPartComponents(part, _deepContext('repeat'))
        ),
        attachment: normalizeValue(
          _mapPartComponents(part, _deepContext('attachment'))
        ),
        fileName: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext({
              value: ['imageInfo', 'fileName'],
            })
          )
        ),
        origFileName: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext({
              value: ['imageInfo', 'origFileName'],
            })
          )
        ),
        top: normalizeValue(
          _mapPartComponents(part, _deepContext(['position', _parseTop]))
        ),
        left: normalizeValue(
          _mapPartComponents(part, _deepContext(['position', _parseLeft]))
        ),
        hidden: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext({
              value: '_hidden',
            })
          )
        ),
      };
    } else if (isGradient) {
      return {
        _type: 'gradient',
        _guid: generateGuid(),
        image: normalizeValue(_mapPartComponents(part, _deepContext('image'))),
        type: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'gradient',
              {
                value: 'type',
                source: 'source',
              },
            ])
          )
        ),
        angle: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'gradient',
              {
                value: 'angle',
                unit: 'unit',
                source: 'source',
              },
            ])
          )
        ),
        size: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'gradient',
              {
                value: 'size',
                source: 'source',
              },
            ])
          )
        ),
        stops: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'gradient',
              {
                value: [
                  'stops',
                  (stops) =>
                    stops.map((stop, i, a) => {
                      const position = (function () {
                        if (stop.pos) {
                          return stop.pos;
                        } else {
                          if (i === 0) {
                            return {value: 0, unit: '%'};
                          } else if (i === a.length - 1) {
                            return {value: 100, unit: '%'};
                          } else {
                            return {value: 50, unit: '%'};
                          }
                        }
                      })();

                      return {
                        position: normalizeValue({
                          local: position,
                        }),
                        color: normalizeValue({
                          local: {value: stop.color},
                        }),
                        _guid: generateGuid(),
                      };
                    }),
                ],
              },
            ])
          )
        ),
        repeat: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'gradient',
              {
                value: [
                  'repeating',
                  (repeating) => (repeating ? 'repeat' : 'no-repeat'),
                ],
                source: 'source',
              },
            ])
          )
        ),
        position: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'gradient',
              {
                value: 'position',
                source: 'source',
              },
            ])
          )
        ),
        hidden: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext({
              value: '_hidden',
            })
          )
        ),
      };
    } else {
      return {
        _type: 'solidcolor',
        _guid: generateGuid(),
        image: normalizeValue(_mapPartComponents(part, _deepContext('image'))),
        color: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext([
              'gradient',
              'stops',
              '0',
              {
                value: 'color',
                source: 'source',
              },
            ])
          )
        ),
        hidden: normalizeValue(
          _mapPartComponents(
            part,
            _deepContext({
              value: '_hidden',
            })
          )
        ),
      };
    }
  });
  const sourcedParts = _decoratePartsSources(mergedParts, 'image');

  return sourcedParts;
}

export function parseFilterParts(value) {
  const parts = FilterParser.parseParts(value);
  const slicedParts = _sliceParts(parts, 'filter');
  const mergedParts = _mergeParts(slicedParts, (part) => {
    return {
      _type: 'filter',
      _guid: generateGuid(),
      blur: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('blur'))
      ),
      dropShadow: normalizeValue(
        _mapPartComponents(part, _deepContext({value: 'dropShadow'}))
      ),
      hueRotate: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('hueRotate'))
      ),
      saturate: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('saturate'))
      ),
      brightness: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('brightness'))
      ),
      contrast: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('contrast'))
      ),
      grayscale: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('grayscale'))
      ),
      sepia: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('sepia'))
      ),
      invert: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('invert'))
      ),
      hidden: normalizeValue(
        _mapPartComponents(
          part,
          _deepContext({
            value: '_hidden',
          })
        )
      ),
    };
  });
  const sourcedParts = _decoratePartsSources(mergedParts, 'blur');

  return sourcedParts;
}

export function parseShadowParts(value, options, variableMap) {
  options = options || {};
  options.target = options.target || 'box';

  const parts = ShadowParser.parseParts(value, variableMap);
  const slicedParts = _sliceParts(parts, 'shadow');
  const mergedParts = _mergeParts(slicedParts, (part) => {
    return {
      _type: 'shadow',
      _guid: generateGuid(),
      _target: options.target,
      position: normalizeValue(
        _mapPartComponents(
          part,
          _deepContext({
            value: ['inset', (inset) => (!inset ? 'outside' : 'inside')],
            source: ['css', 'source'],
          })
        )
      ),
      x: normalizeValue(_mapPartComponents(part, _VUSourceDeepContext('x'))),
      y: normalizeValue(_mapPartComponents(part, _VUSourceDeepContext('y'))),
      blur: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('blur'))
      ),
      spread: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('spread'))
      ),
      color: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('color'))
      ),
      hidden: normalizeValue(
        _mapPartComponents(
          part,
          _deepContext({
            value: '_hidden',
          })
        )
      ),
    };
  });
  // FIXME: Why is this `'angle'`? Shouldn't this be something like
  // `'boxShadow'` or `'textShadow'`?
  const sourcedParts = _decoratePartsSources(mergedParts, 'angle');

  return sourcedParts;
}

export function parseTransitionParts(value) {
  const parts = TransitionParser.parseParts(value);
  const slicedParts = _sliceParts(parts, 'transition');
  const mergedParts = _mergeParts(slicedParts, (part) => {
    return {
      _type: 'transition',
      _guid: generateGuid(),
      property: normalizeValue(
        _mapPartComponents(part, _deepContext('property'))
      ),
      duration: normalizeValue(
        _mapPartComponents(part, _deepContext('duration'))
      ),
      timing: normalizeValue(_mapPartComponents(part, _deepContext('timing'))),
      hidden: normalizeValue(
        _mapPartComponents(
          part,
          _deepContext({
            value: '_hidden',
          })
        )
      ),
    };
  });
  const sourcedParts = _decoratePartsSources(mergedParts, 'property');

  return sourcedParts;
}

export function parseTransformParts(value) {
  const parts = TransformParser.parseParts(value);
  const slicedParts = _sliceParts(parts, 'transform');
  const mergedParts = _mergeParts(slicedParts, (part) => {
    return {
      _type: 'transform',
      _guid: generateGuid(),
      rotateX: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('rotateX'))
      ),
      rotateY: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('rotateY'))
      ),
      rotateZ: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('rotateZ'))
      ),
      translateX: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('translateX'))
      ),
      translateY: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('translateY'))
      ),
      translateZ: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('translateZ'))
      ),
      scaleX: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('scaleX'))
      ),
      scaleY: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('scaleY'))
      ),
      scaleZ: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('scaleZ'))
      ),
      skewX: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('skewX'))
      ),
      skewY: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('skewY'))
      ),
      perspective: normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext('perspective'))
      ),
      hidden: normalizeValue(
        _mapPartComponents(
          part,
          _deepContext({
            value: '_hidden',
          })
        )
      ),
    };
  });
  const sourcedParts = _decoratePartsSources(mergedParts, 'transform');

  return sourcedParts;
}

export function parseFontVariationSettingsParts(value, axes) {
  const parts = FontVariationSettingsParser.parseParts(value);
  const slicedParts = _sliceParts(parts, 'fontVariationSettings');
  const mergedParts = _mergeParts(slicedParts, (part) => {
    const axesMap = {};
    axes.forEach((axisTag) => {
      axesMap[axisTag] = normalizeValue(
        _mapPartComponents(part, _VUSourceDeepContext(axisTag))
      );
    });

    return {
      _type: 'fontVariationSettings',
      _guid: generateGuid(),
      // Instead of hardcoding parts (like other multi-part properties), the font variation
      // parts are derived from the variable font family's axes
      ...axesMap,

      hidden: normalizeValue(
        _mapPartComponents(
          part,
          _deepContext({
            value: '_hidden',
          })
        )
      ),
    };
  });

  const sourcedParts = _decoratePartsSources(
    mergedParts,
    'fontVariationSettings'
  );

  return sourcedParts;
}

// Helpers

// Merges already parsed parts' components together
// This correlates subvalues of the local/upstream components to point to the corresponding subvalue in the other component
// e.g. it sets the upstream value of the local background-position for a part to the upstream value of the background-position for that part
// @return {Part[]}
function _mergeParts(parts, merger) {
  function mergeComponent(component) {
    return {
      value: (() => {
        if (component) {
          const result = component.map(merger);
          if (component.source) {
            result.source = component.source; // keep the source decoration from the parser
          }
          return result;
        } else {
          return component;
        }
      })(),
      unit: null,
      source: component ? component.source || null : null,
    };
  }

  const upstreamComponent = (function () {
    const upstream = parts.upstream;

    if (upstream) {
      const mappedUpstream = upstream.map(function (part) {
        return {
          local: null,
          upstream: part.upstream,
          browser: part.browser,
        };
      });
      mappedUpstream.source = upstream.source;

      return mappedUpstream;
    } else {
      return upstream;
    }
  })();

  return {
    local: mergeComponent(parts.local),
    upstream: mergeComponent(upstreamComponent),
    downstream: mergeComponent(parts.downstream),
  };
}

// General purpose object transformer
// keypath-like schema => source object => transformed object according to the schema
// Object[] path
// @return {Object => Object}
function _deepContext(path) {
  if (!Array.isArray(path)) {
    path = [path];
  }

  function _mapContext(ctx, next) {
    if (typeof next === 'string') {
      if (ctx != null) {
        ctx = ctx[next];
      }
    } else if (typeof next === 'function') {
      ctx = next(ctx);
    } else if (typeof next === 'object') {
      const result = {};
      for (const key in next) {
        if (ctx != null) {
          result[key] = _deepContext(next[key])(ctx);
        } else {
          result[key] = null;
        }
      }
      ctx = result;
    }

    if (ctx === undefined) {
      ctx = null;
    }

    return ctx;
  }

  return (ctx) => {
    for (let i = 0; i < path.length; i++) {
      const next = path[i];
      ctx = _mapContext(ctx, next);
    }
    return ctx;
  };
}

// Map over {local,upstream,downstream,browser} components by applying a function to each
// @return {Part}
function _mapPartComponents(part, fn) {
  return _deepContext([
    {
      local: [LOCAL, fn],
      upstream: [UPSTREAM, fn],
      downstream: [DOWNSTREAM, fn],
      browser: [BROWSER, fn],
    },
  ])(part);
}

// Shortcut for converting the property values emitted from CssParser into {value,unit,source} value shape used by StyleStore
function _VUSourceDeepContext(property) {
  return _deepContext({
    value: [property, 'value'],
    unit: [property, 'unit'],
    source: ['css', 'source'],
  });
}
